import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants for smooth transitions
export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const fadeInDown = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

export const slideIn = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 }
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 }
};

export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05
    }
  }
};

export const progressBar = {
  initial: { width: 0 },
  animate: (width) => ({
    width: `${width}%`,
    transition: {
      duration: 0.8,
      ease: 'easeInOut'
    }
  })
};

// Animated wrapper components for operations
export const AnimatedOperationsPanel = ({ children, isVisible, ...props }) => (
  <AnimatePresence>
    {isVisible && (
      <motion.div
        initial="initial"
        animate="animate"
        exit="exit"
        variants={fadeInUp}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        {...props}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

export const AnimatedFileItem = ({ children, index = 0, ...props }) => (
  <motion.div
    initial="initial"
    animate="animate"
    exit="exit"
    variants={slideIn}
    transition={{ duration: 0.2, delay: index * 0.02 }}
    layout
    {...props}
  >
    {children}
  </motion.div>
);

export const AnimatedProgressSegment = ({ children, delay = 0, ...props }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    animate={{ scaleX: 1 }}
    exit={{ scaleX: 0 }}
    transition={{ 
      duration: 0.5, 
      delay,
      ease: 'easeInOut',
      transformOrigin: 'left'
    }}
    {...props}
  >
    {children}
  </motion.div>
);

export const AnimatedStatusBadge = ({ children, status, ...props }) => {
  const getStatusAnimation = () => {
    switch (status) {
      case 'processing':
        return {
          animate: {
            scale: [1, 1.1, 1],
            transition: {
              duration: 1,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }
        };
      case 'completed':
        return {
          initial: { scale: 0 },
          animate: { scale: 1 },
          transition: { 
            type: 'spring',
            stiffness: 500,
            damping: 30
          }
        };
      case 'failed':
        return {
          animate: {
            x: [-2, 2, -2, 2, 0],
            transition: {
              duration: 0.4,
              ease: 'easeInOut'
            }
          }
        };
      default:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.2 }
        };
    }
  };

  return (
    <motion.div
      {...getStatusAnimation()}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedProcessingIndicator = ({ isActive, children, ...props }) => (
  <motion.div
    animate={{
      opacity: isActive ? [0.5, 1, 0.5] : 1,
      scale: isActive ? [1, 1.05, 1] : 1
    }}
    transition={{
      duration: isActive ? 1.5 : 0.3,
      repeat: isActive ? Infinity : 0,
      ease: 'easeInOut'
    }}
    {...props}
  >
    {children}
  </motion.div>
);